import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLayout } from '../../contexts/LayoutContext';
import { GlobalHeader, type GlobalHeaderProps } from '../GlobalHeader';
import { ContextPane } from './ContextPane';
import { LeftNavPane } from './LeftNavPane';
import { MainPane } from './MainPane';
import { RightRail } from './RightRail';
import { RightUtilityPane } from './RightUtilityPane';
import { useModuleChatRailItem } from './useModuleChatRailItem';

/**
 * RightRailItem - configuration for a single item in the project right rail
 *
 * Each item represents one toggleable right-pane menu. Pages build an array of
 * these and pass it to AppShell via `rightRailItems`. The id must be a valid
 * RightPaneMode value so that LayoutContext can track the active selection.
 */
export interface RightRailItem {
  /** Unique identifier; also used as RightPaneMode when the item is active */
  id: string;
  /** Tooltip label shown when hovering the rail icon */
  label: string;
  /** Icon element rendered in the rail (16–18px SVG recommended) */
  icon: React.ReactNode;
  /** When true, show a visual badge on the rail icon to indicate edit mode */
  isEditing?: boolean;
  /**
   * When false the item is hidden from the rail (and pane).
   * Omit or set true to show the item. Default: true.
   */
  enabled?: boolean;
  /** Content rendered inside the right pane when this item is active */
  content: React.ReactNode;
  /** When true, the active pane content stays mounted but renders in skeleton mode. */
  loading?: boolean;
  /**
   * Optional element rendered in the pane header's right side.
   * Useful for per-menu action buttons. Omit when the menu has no action.
   */
  headerAction?: React.ReactNode;
  /**
   * Optional override for the pane header's left-side title. Falls back to
   * `label` when omitted. Used by the AI chat to show the active session
   * title while the rail tooltip keeps the assistant name.
   */
  headerTitle?: React.ReactNode;
  /**
   * When true, removes padding from the content wrapper and makes it a flex column
   * with overflow hidden - the child component is expected to manage its own scroll.
   */
  noPadding?: boolean;
  /**
   * Marks this item as THE module copilot chat. When a page provides its own
   * (DealWorkspace wires page-specific card handlers), the shell skips appending
   * the global one so there is never a duplicate chat icon.
   */
  isModuleChat?: boolean;
}

export interface AppShellProps {
  /**
   * Header content. When omitted, renders the shared GlobalHeader automatically.
   * Pass explicit content only for pages that need a custom header layout.
   */
  header?: React.ReactNode;

  /** Forwarded to GlobalHeader when `header` is omitted: override the identity label. */
  headerIdentity?: GlobalHeaderProps['identity'];
  /** Forwarded to GlobalHeader when `header` is omitted: hide the live meeting badge. */
  headerShowBadge?: GlobalHeaderProps['showBadge'];
  /** Forwarded to GlobalHeader when `header` is omitted: hide the module selector. */
  headerShowModuleSelector?: GlobalHeaderProps['showModuleSelector'];

  /** LeftNavPane content (primary navigation) */
  leftNav: React.ReactNode;

  /** ContextPane content (contextual filters/nav) */
  contextPane?: React.ReactNode;
  /** Optional className forwarded to the ContextPane's ResizableDivider (e.g. for header border bridge). */
  contextPaneDividerClassName?: string;
  /**
   * When true, the ContextPane is in a collapsed (icon-only) state.
   * Omits the divider grid column and hides the ResizableDivider.
   */
  contextPaneCollapsed?: boolean;

  /** MainPane content (primary workspace) */
  main: React.ReactNode;

  /** BottomPane content within MainPane (logs, console, etc.) */
  bottomPane?: React.ReactNode;

  /**
   * Right rail items. When provided, renders the right rail strip. When an item
   * is selected, also renders the right pane content area (resizable, with a header).
   */
  rightRailItems?: RightRailItem[];

  /**
   * Opt out of the global AI module-chat rail item. Module landing pages set
   * this (they have their own chat surface or none); all other pages get the
   * AI copilot in the rail automatically.
   */
  suppressModuleChat?: boolean;
}

/**
 * AppShell - enterprise multi-pane operational shell
 *
 * Layout structure (frontend-architecture.md §7):
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │                              Header                                   │
 * ├──────────────┬─────────┬─────────────────┬────────────────┬──────────┤
 * │              │         │                 │                │          │
 * │ LeftNavPane  │ Context │   MainPane      │  RightPane     │ RightRail│
 * │              │  Pane   │                 │  (when open)   │ (narrow) │
 * │              │         │ ─────────────── │                │          │
 * │              │         │   BottomPane    │                │          │
 * └──────────────┴─────────┴─────────────────┴────────────────┴──────────┘
 *
 * RightRail is always visible when rightRailItems are present.
 * RightPane is only visible when the user selects an item in the rail.
 * A resizable divider sits between MainPane and RightPane.
 */
export function AppShell({
  header,
  headerIdentity,
  headerShowBadge,
  headerShowModuleSelector,
  leftNav,
  contextPane,
  contextPaneDividerClassName,
  contextPaneCollapsed,
  main,
  bottomPane,
  rightRailItems,
  suppressModuleChat,
}: AppShellProps) {
  const {
    leftNavCollapsed,
    contextPaneVisible,
    rightPaneVisible,
    rightPaneMode,
    setRightPaneMode,
    setRightPaneVisible,
  } = useLayout();

  // Global AI module-chat item: appended to every page's rail unless the page
  // opts out or already provides its own module chat (isModuleChat).
  const moduleChatItem = useModuleChatRailItem();
  const effectiveRailItems = React.useMemo(() => {
    if (suppressModuleChat || !moduleChatItem) return rightRailItems;
    if (rightRailItems?.some((item) => item.isModuleChat)) return rightRailItems;
    return [...(rightRailItems ?? []), moduleChatItem];
  }, [suppressModuleChat, moduleChatItem, rightRailItems]);

  // Navigation can strand rightPaneMode on an id the current page's items no
  // longer contain (e.g. 'chat' on DealWorkspace → a page with only 'module-chat').
  // Close the pane instead of rendering an empty shell. useLayoutEffect (not
  // useEffect) so the stranded mode is reconciled before paint - no flicker frame
  // showing the previous page's pane.
  React.useLayoutEffect(() => {
    if (!rightPaneVisible || !rightPaneMode) return;
    const exists = effectiveRailItems?.some(
      (item) => item.id === rightPaneMode && item.enabled !== false
    );
    if (exists) return;
    // The global 'module-chat' item disappears when a page provides its own
    // module chat (isModuleChat) - transfer the open pane to it instead of
    // closing (chat-navigation continuity onto deal pages).
    const moduleChat =
      rightPaneMode === 'module-chat'
        ? effectiveRailItems?.find((item) => item.isModuleChat && item.enabled !== false)
        : undefined;
    setRightPaneMode(moduleChat ? moduleChat.id : null);
  }, [rightPaneVisible, rightPaneMode, effectiveRailItems, setRightPaneMode]);

  // AI-driven navigation continuity: when a chat navigation lands here with
  // { state: { openCopilotRail: true } }, open the destination page's module-chat
  // pane so the conversation visibly continues in the right rail. The state flag
  // is cleared immediately so back/forward doesn't re-open the pane.
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const flagged = (location.state as { openCopilotRail?: boolean } | null)?.openCopilotRail;
    if (!flagged) return;
    const moduleChat = effectiveRailItems?.find(
      (item) => item.isModuleChat && item.enabled !== false
    );
    if (moduleChat) {
      setRightPaneMode(moduleChat.id);
      setRightPaneVisible(true);
    }
    const { openCopilotRail: _consumed, ...rest } = (location.state ?? {}) as Record<
      string,
      unknown
    >;
    navigate(location.pathname, { replace: true, state: rest });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Suppress grid-template-columns transition during mount so that layout effects
  // (e.g. LiveMeetingPage's useLayoutEffect setting rightPaneVisible) that cause
  // a second DOM commit before first paint don't trigger a visible CSS animation.
  const bodyRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    bodyRef.current?.removeAttribute('data-mounting');
  }, []);

  // Rail is visible when items are configured - prop-driven, no async context flag needed
  const hasVisibleRailItems =
    !!effectiveRailItems && effectiveRailItems.some((item) => item.enabled !== false);
  const showRail = hasVisibleRailItems;

  // Right pane content area opens when a user selects a rail item
  const showRightPane = rightPaneVisible && hasVisibleRailItems;

  // ContextPane columns are only valid when the page actually provides content.
  // If contextPane prop is absent, contextPaneVisible from a *previous* page
  // would otherwise leave two empty grid columns, pushing MainPane into the
  // wrong (auto-sized) column and causing the content-not-full-width bug.
  const hasContextPane = contextPane != null;

  // Build grid template columns based on which panes are currently visible
  const gridTemplateColumns = React.useMemo(() => {
    const columns: string[] = [];

    // LeftNavPane: expanded or icon-only collapsed
    columns.push(
      leftNavCollapsed
        ? 'var(--pane-leftnav-width-collapsed)'
        : 'var(--pane-leftnav-width-expanded)'
    );

    // ContextPane: width is set via inline style on the aside; use 'auto' here
    // so the grid column tracks the child's intrinsic width.
    // Only add columns when contextPane content is actually provided - avoids
    // allocating grid space that leaves MainPane in an auto-sized column.
    // When contextPaneCollapsed, omit the divider column so the icon strip
    // sits flush (no gap) against the MainPane.
    if (contextPaneVisible && hasContextPane) {
      columns.push('auto');
      if (!contextPaneCollapsed) {
        columns.push('var(--divider-hit-area)');
      }
    }

    // MainPane: fills remaining space
    columns.push('1fr');

    // RightPane: left-side divider + dynamic width (set via inline style)
    if (showRightPane) {
      columns.push('var(--divider-hit-area)');
      columns.push('auto');
    }

    // RightRail: narrow fixed strip, always present when enabled
    if (showRail) {
      columns.push('var(--pane-rightrail-width)');
    }

    return columns.join(' ');
  }, [
    leftNavCollapsed,
    contextPaneVisible,
    hasContextPane,
    contextPaneCollapsed,
    showRightPane,
    showRail,
  ]);

  return (
    <div className="app-shell">
      {/* Header */}
      <header className="app-shell-header" role="banner">
        {header ?? (
          <GlobalHeader
            identity={headerIdentity}
            showBadge={headerShowBadge}
            showModuleSelector={headerShowModuleSelector}
          />
        )}
      </header>

      {/* Body grid */}
      <div
        ref={bodyRef}
        className="app-shell-body"
        data-mounting=""
        style={{ gridTemplateColumns }}
      >
        <LeftNavPane>{leftNav}</LeftNavPane>

        {contextPaneVisible && contextPane && (
          <ContextPane
            dividerClassName={contextPaneDividerClassName}
            hideDivider={contextPaneCollapsed}
          >
            {contextPane}
          </ContextPane>
        )}

        <MainPane bottomPaneContent={bottomPane}>{main}</MainPane>

        {/* Right pane: only mounted when a rail item is selected */}
        {effectiveRailItems && showRightPane && <RightUtilityPane items={effectiveRailItems} />}

        {/* Right rail: always visible while enabled, drives pane open/close/switch */}
        {showRail && <RightRail items={effectiveRailItems!} />}
      </div>
    </div>
  );
}
